import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import styles from './ToastContainer.module.scss';
// import ToastNotification from './ToastNotification.tsx.legacy';
const ToastNotification = ({ onClose, ...props }: any) => (
  <div style={{ padding: '1rem', background: '#333', color: 'white', borderRadius: '8px', margin: '0.5rem' }}>
    {props.title && <strong>{props.title}: </strong>}
    {props.message}
    <button onClick={() => onClose?.(props.id)} style={{ marginLeft: '1rem' }}>×</button>
  </div>
);

// Temporary stub types
interface ToastProps {
  id?: string;
  title?: string;
  message?: string;
  type?: 'success' | 'error' | 'warning' | 'info';
  onClose?: () => void;
}

export interface Toast extends Omit<ToastProps, 'onClose'> {
  id: string;
}

interface ToastContextType {
  addToast: (toast: Omit<Toast, 'id'>) => string;
  removeToast: (id: string) => void;
  clearToasts: () => void;
  toasts: Toast[];
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

interface ToastProviderProps {
  children: ReactNode;
  maxToasts?: number;
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ 
  children, 
  maxToasts = 5,
  position = 'top-right' 
}) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback((toast: Omit<Toast, 'id'>) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newToast: Toast = { ...toast, id };
    
    setToasts(current => {
      const updated = [newToast, ...current];
      // Keep only the most recent toasts if we exceed the limit
      return updated.slice(0, maxToasts);
    });
    
    return id;
  }, [maxToasts]);

  const removeToast = useCallback((id: string) => {
    setToasts(current => current.filter(toast => toast.id !== id));
  }, []);

  const clearToasts = useCallback(() => {
    setToasts([]);
  }, []);

  const contextValue: ToastContextType = {
    addToast,
    removeToast,
    clearToasts,
    toasts
  };

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      {typeof document !== 'undefined' && createPortal(
        <ToastContainer 
          toasts={toasts} 
          onRemove={removeToast}
          position={position}
        />,
        document.body
      )}
    </ToastContext.Provider>
  );
};

interface ToastContainerProps {
  toasts: Toast[];
  onRemove: (id: string) => void;
  position: string;
}

const ToastContainer: React.FC<ToastContainerProps> = ({ toasts, onRemove, position }) => {
  if (toasts.length === 0) return null;

  return (
    <div 
      className={`${styles.toastContainer} ${styles[position.replace('-', '')]}`}
      aria-live="polite"
      aria-label="Notifications"
    >
      {toasts.map(toast => (
        <ToastNotification
          key={toast.id}
          {...toast}
          onClose={onRemove}
        />
      ))}
    </div>
  );
};

// Convenience hooks for different toast types
export const useSuccessToast = () => {
  const { addToast } = useToast();
  return useCallback((title: string, message: string, options?: Partial<Toast>) => {
    return addToast({ type: 'success', title, message, ...options });
  }, [addToast]);
};

export const useErrorToast = () => {
  const { addToast } = useToast();
  return useCallback((title: string, message: string, options?: Partial<Toast>) => {
    return addToast({ type: 'error', title, message, ...options });
  }, [addToast]);
};

export const useWarningToast = () => {
  const { addToast } = useToast();
  return useCallback((title: string, message: string, options?: Partial<Toast>) => {
    return addToast({ type: 'warning', title, message, ...options });
  }, [addToast]);
};

export const useInfoToast = () => {
  const { addToast } = useToast();
  return useCallback((title: string, message: string, options?: Partial<Toast>) => {
    return addToast({ type: 'info', title, message, ...options });
  }, [addToast]);
};

export const useSubscriptionErrorToast = () => {
  const { addToast } = useToast();
  return useCallback((error: Error, onUpgrade?: () => void) => {
    return addToast({ 
      type: 'error', 
      title: 'Error', 
      message: error.message
      // Note: Error details and upgrade button handled separately
    });
  }, [addToast]);
};

export default ToastProvider;